import * as React from 'react';
import * as styled from './styled';
import {observer} from 'mobx-react';
import UserStore from 'stores/UserStore';
import {CourseObject, getCourseByID} from 'api/course';
import useModal from 'hooks/use-modal';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import InstancePicker, {InstanceType} from 'components/InstancePicker';
import {FavoriteCourseProps} from './types';

/**
 * The favorite course selector.
 * @param {FavoriteCourseProps} props
 * @return {React.ReactElement<FavoriteCourseProps>}
 * @constructor
 */
function FavoriteCourse(
    props: FavoriteCourseProps,
): React.ReactElement<FavoriteCourseProps> {
  const {
    isModalVisible, setModalVisible,
  } = useModal();

  const [
    course, setCourse,
  ] = React.useState<CourseObject | undefined>(undefined);

  /**
   * Fetch the course.
   */
  function fetchCourse(): void {
    if (UserStore.state?.favoriteCourseId !== undefined) {
      getCourseByID(UserStore.state?.favoriteCourseId).then((response) => {
        validateResponse(response);

        if (response && !isError(response)) {
          setCourse(response);
        }
      });
    }
  }

  React.useEffect(() => {
    fetchCourse();
  }, []);

  /**
   * Render title.
   * @return {JSX.Element | undefined}
   */
  function renderTitle(): JSX.Element | undefined {
    return !course ? (
      <styled.Title>
        Не назначен
      </styled.Title>
    ) : (
      <styled.Title>
        {course.title}
      </styled.Title>
    );
  }

  /**
   * Render modal.
   * @return {JSX.Element | undefined}
   */
  function renderModal(): JSX.Element | undefined {
    /**
     * The select event handler.
     * @param {CourseObject[]} selected
     */
    function handleSelect(selected: CourseObject['id'][]): void {
      hideModal();
      UserStore.state && UserStore.save({
        ...UserStore.state,
        favoriteCourseId: selected[0],
      }).then(() => {
        fetchCourse();
      });
    }

    return isModalVisible ? (
      <InstancePicker<CourseObject>
        type={InstanceType.Course}
        renderHeader={<span>Выберите курс</span>}
        onBlur={() => hideModal()}
        onSelect={handleSelect}
      />
    ) : undefined;
  }

  /**
   * Open the modal.
   */
  function showModal(): void {
    setModalVisible(true);
  }

  /**
   * Close the modal.
   */
  function hideModal(): void {
    setModalVisible(false);
  }

  /**
   * Get the content.
   * @return {JSX.Element}
   */
  function getContent(): JSX.Element {
    return !props.isToggle ? (
      <styled.Button onClick={() => showModal()}>
        <styled.Wrapper isToggle={props.isToggle}>
          <styled.Icon />
        </styled.Wrapper>
      </styled.Button>
    ) : (
      <styled.Wrapper isToggle={props.isToggle}>
        <styled.Icon />
        <styled.Label>
          Текущий курс
        </styled.Label>
        <styled.Button onClick={() => showModal()}>
          Изменить
        </styled.Button>
        {renderTitle()}
      </styled.Wrapper>
    );
  }

  return (
    <React.Fragment>
      {getContent()}
      {renderModal()}
    </React.Fragment>
  );
}

export default observer(FavoriteCourse);
